import { defaultRecurrenceValue } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm';
import { RecurrenceValues } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm.types';
import { CreateEventDto } from 'src/Services/API/generated';

type BackendRecurrence = NonNullable<CreateEventDto['recurrence']>;

export const transformRecurrenceDtoToFormValues = (
  recurrence: CreateEventDto['recurrence']
): RecurrenceValues => {
  // If no recurrence return default values
  if (!recurrence) {
    return defaultRecurrenceValue;
  }

  const formValues: RecurrenceValues = {
    frequency: recurrence.interval ? 'CUSTOM' : recurrence.frequency,
    customFrequency: recurrence.interval ? recurrence.frequency : undefined,
    interval: recurrence.interval?.toString() || '1',
    byMonthDays: [],
    byMonth: [],
    byMonthDay: '',
    byWeekDay: '',
    byWeekDays: [],
    monthDayNumberOrAdvanced: 'number',
    monthDaySelected: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    dtstart: recurrence.dtstart,
    until: recurrence.until?.toDate(),
  };

  // If recurrence is custom handle properties
  if (recurrence.interval) {
    const handlers = {
      YEARLY: handleYearlyToFormValues,
      MONTHLY: handleMonthlyToFormValues,
      WEEKLY: handleWeeklyToFormValues,
    };

    const handler = handlers[recurrence.frequency as keyof typeof handlers];
    if (handler) {
      Object.assign(formValues, handler(recurrence));
    }
  }

  return formValues;
};

const handleYearlyToFormValues = (recurrence: BackendRecurrence): Partial<RecurrenceValues> => {
  const formValues: Partial<RecurrenceValues> = {
    byMonth: recurrence.byMonth ?? [],
    byMonthDay: '1',
  };

  const advancedSelection = recurrence.byWeekDay;

  if (advancedSelection) {
    formValues.monthDaySelected = true;
    formValues.byWeekDay = recurrence.byWeekDay ? recurrence.byWeekDay[0].toString() : '';
    formValues.byMonthDay = recurrence.bySetPos?.toString();
  }

  return formValues;
};

const handleMonthlyToFormValues = (recurrence: BackendRecurrence): Partial<RecurrenceValues> => {
  const hasAdvancedRules = recurrence.byWeekDay !== undefined;

  const formValues: Partial<RecurrenceValues> = {
    monthDayNumberOrAdvanced: hasAdvancedRules ? 'advanced' : 'number',
  };

  if (hasAdvancedRules) {
    formValues.byWeekDay = recurrence.byWeekDay ? recurrence.byWeekDay[0].toString() : '';
    formValues.byMonthDay = recurrence.bySetPos?.toString();
  } else {
    formValues.byMonthDays = recurrence.byMonthDay?.map(monthDay => monthDay.toString()) ?? [];
  }

  return formValues;
};

const handleWeeklyToFormValues = (recurrence: BackendRecurrence): Partial<RecurrenceValues> => ({
  byWeekDays: recurrence.byWeekDay ? recurrence.byWeekDay.map(weekDay => weekDay.toString()) : [],
});
